// mutators.ts
import type { CustomMutatorDefs, Transaction, InsertValue, UpdateValue, UpsertValue, DeleteID } from '@rocicorp/zero';
import { schema } from './schema';
import type { Octokit } from 'octokit';
import type { ReactionEmoji } from '../reactions';

// Define AppSchema and TableName based on the imported schema
type AppSchema = typeof schema;
type TableName = keyof AppSchema['tables'];

export type AddIssueCommentReactionPayload = {
  gid: string;
  commentId: number;
  emoji: ReactionEmoji;
  userId: number;
  owner: string;
  repo: string;
};

export type RemoveIssueCommentReactionPayload = {
  gid: string;
  owner: string;
  repo: string;
};

export type AddReviewCommentReactionPayload = {
  gid: string;
  commentId: number;
  emoji: ReactionEmoji;
  userId: number;
  owner: string;
  repo: string;
};

export type RemoveReviewCommentReactionPayload = {
  gid: string;
  owner: string;
  repo: string;
};

export type AddReviewBodyReactionPayload = {
  gid: string;
  reviewId: number;
  pullNumber: number;
  emoji: ReactionEmoji;
  userId: number;
  owner: string;
  repo: string;
};

export type RemoveReviewBodyReactionPayload = {
  gid: string;
  pullNumber: number;
  owner: string;
  repo: string;
};

const crud = <
  TName extends Extract<TableName, 'PullsTable' | 'IssueCommentsTable' | 'ReviewCommentsTable' | 'ReactionsTable' | 'UserPreferencesTable'>,
>(
  tableName: TName
) => {
  type Table = AppSchema['tables'][TName];

  return {
    // Use the specific Table type for InsertValue, UpdateValue, UpsertValue
    insert: async (tx: Transaction<AppSchema>, values: InsertValue<Table>) =>
      (tx.mutate[tableName].insert as (v: InsertValue<Table>) => Promise<void>)(values),
    update: async (tx: Transaction<AppSchema>, values: UpdateValue<Table>) =>
      (tx.mutate[tableName].update as (v: UpdateValue<Table>) => Promise<void>)(values),
    upsert: async (tx: Transaction<AppSchema>, values: UpsertValue<Table>) =>
      (tx.mutate[tableName].upsert as (v: UpsertValue<Table>) => Promise<void>)(values),
    delete: async (tx: Transaction<AppSchema>, id: DeleteID<Table>) =>
      (tx.mutate[tableName].delete as (i: DeleteID<Table>) => Promise<void>)(id),
  } as const;
};

export function createMutators({ octokit }: { octokit: Octokit }) {
  return {
    OrgConfigTable: {
      update: async (tx, payload: { owner: string; key: string[]; value: any }) => {
        console.log('payload', payload);
        const currentOrgConfig = await tx.query.OrgConfigTable.where('owner', payload.owner).one();
        console.log('currentOrgConfig', currentOrgConfig);
        if (!currentOrgConfig) {
          throw new Error(`Org config not found: ${payload.owner}`);
        }

        // Build a new config object with the nested value replaced based on the
        // dot-delimited key path (e.g. "pulls.description.enabled").
        const setAtPath = (obj: any, segments: string[], val: any): any => {
          if (segments.length === 0) return val;
          const [head, ...rest] = segments;
          return {
            ...(obj ?? {}),
            [head]: setAtPath((obj ?? {})[head], rest, val),
          };
        };

        const updatedConfig = setAtPath(currentOrgConfig.config, payload.key, payload.value);

        await tx.mutate.OrgConfigTable.update({
          owner: payload.owner,
          config: updatedConfig,
        });
      },
    },
    PullsTable: {
      ...crud('PullsTable'),
      submitReview: async (
        tx,
        values: {
          nodeId: string;
          owner: string;
          repo: string;
          number: number;
          body: string;
          event: 'APPROVE' | 'REQUEST_CHANGES';
        }
      ) => {
        await tx.mutate.PullsTable.update({
          nodeId: values.nodeId,
          reviewDecision: values.event === 'APPROVE' ? 'APPROVED' : 'CHANGES_REQUESTED',
        });
      },
      openOrClose: async (
        tx,
        values: { nodeId: string; owner: string; repo: string; number: number; event: 'OPEN' | 'CLOSE' }
      ) => {
        await tx.mutate.PullsTable.update({
          nodeId: values.nodeId,
          state: values.event === 'OPEN' ? 'OPEN' : 'CLOSED',
        });
      },
      publish: async (tx, values: { nodeId: string; owner: string; repo: string; number: number }) => {
        await tx.mutate.PullsTable.update({
          nodeId: values.nodeId,
          state: 'OPEN',
        });
      },
      convertToDraft: async (tx, values: { nodeId: string; owner: string; repo: string; number: number }) => {
        await tx.mutate.PullsTable.update({
          nodeId: values.nodeId,
          state: 'DRAFT',
        });
      },
    },
    IssueCommentsTable: crud('IssueCommentsTable'),
    ReactionsTable: {
      ...crud('ReactionsTable'),

      // Issue comment reactions
      addIssueCommentReaction: async (tx, values: AddIssueCommentReactionPayload) => {
        // Check if reaction already exists
        const existingReaction = await tx.query.ReactionsTable.where(({ cmp, and }) =>
          and(
            cmp('commentId', values.commentId),
            cmp('commentType', 'issue'),
            cmp('userId', values.userId),
            cmp('emoji', values.emoji)
          )
        ).one();

        // Reaction already exists, don't add duplicate
        if (existingReaction) return;

        await tx.mutate.ReactionsTable.insert({
          gid: values.gid,
          id: null, // Will be set when synced to GitHub
          commentId: values.commentId,
          commentType: 'issue',
          userId: values.userId,
          emoji: values.emoji,
        });
      },

      removeIssueCommentReaction: async (tx, values: RemoveIssueCommentReactionPayload) => {
        await tx.mutate.ReactionsTable.delete({ gid: values.gid });
      },

      // Review comment reactions
      addReviewCommentReaction: async (tx, values: AddReviewCommentReactionPayload) => {
        // Check if reaction already exists
        const existingReaction = await tx.query.ReactionsTable.where(({ cmp, and }) =>
          and(
            cmp('commentId', values.commentId),
            cmp('commentType', 'review'),
            cmp('userId', values.userId),
            cmp('emoji', values.emoji)
          )
        ).one();

        // Reaction already exists, don't add duplicate
        if (existingReaction) return;

        await tx.mutate.ReactionsTable.insert({
          gid: values.gid,
          id: null, // Will be set when synced to GitHub
          commentId: values.commentId,
          commentType: 'review',
          userId: values.userId,
          emoji: values.emoji,
        });
      },

      removeReviewCommentReaction: async (tx, values: RemoveReviewCommentReactionPayload) => {
        await tx.mutate.ReactionsTable.delete({ gid: values.gid });
      },

      // Review body reactions
      addReviewBodyReaction: async (tx, values: AddReviewBodyReactionPayload) => {
        // Check if reaction already exists
        const existingReaction = await tx.query.ReactionsTable.where(({ cmp, and }) =>
          and(
            cmp('commentId', values.reviewId),
            cmp('commentType', 'review_body'),
            cmp('userId', values.userId),
            cmp('emoji', values.emoji)
          )
        ).one();

        // Reaction already exists, don't add duplicate
        if (existingReaction) return;

        await tx.mutate.ReactionsTable.insert({
          gid: values.gid,
          id: null, // Will be set when synced to GitHub
          commentId: values.reviewId,
          commentType: 'review_body',
          userId: values.userId,
          emoji: values.emoji,
        });
      },

      removeReviewBodyReaction: async (tx, values: RemoveReviewBodyReactionPayload) => {
        await tx.mutate.ReactionsTable.delete({ gid: values.gid });
      },
    },
    ReviewCommentsTable: {
      ...crud('ReviewCommentsTable'),
      resolveComment: async (
        tx,
        values: {
          commentId: number;
          resolvedById: number;
          owner: string;
          repo: string;
          pullNumber: number;
        }
      ) => {
        // Find the comment to get its gid (primary key for Zero)
        const comment = await tx.query.ReviewCommentsTable.where(({ cmp }) => cmp('id', values.commentId)).one();

        if (!comment) {
          throw new Error(`Comment not found: ${values.commentId}`);
        }

        // Optimistic update - mark comment as resolved
        await tx.mutate.ReviewCommentsTable.update({
          gid: comment.gid,
          resolvedById: values.resolvedById,
        });
      },
      unresolveComment: async (
        tx,
        values: {
          commentId: number;
          owner: string;
          repo: string;
          pullNumber: number;
        }
      ) => {
        // Find the comment to get its gid (primary key for Zero)
        const comment = await tx.query.ReviewCommentsTable.where(({ cmp }) => cmp('id', values.commentId)).one();

        if (!comment) {
          throw new Error(`Comment not found: ${values.commentId}`);
        }

        // Optimistic update - mark comment as unresolved
        await tx.mutate.ReviewCommentsTable.update({
          gid: comment.gid,
          resolvedById: null,
        });
      },
    },
  } as const satisfies CustomMutatorDefs<typeof schema>;
}

export type Mutators = ReturnType<typeof createMutators>;
